import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CurrencyContextService } from 'src/app/core/services/general/currency-context.service';

/**
 * "How are you paying?" — the currency an operator picks at the moment a charge is CREATED.
 *
 * Tatenda, 2026-10-03: *"we want to have zig everywhere, and lets not do short cuts… they have to ask, how are
 * you paying, on gate pass too when ticking the gatepass nbeeds to have additional step of usd or zig."*
 *
 * ── WHY THIS IS A CORRECTNESS CONTROL AND NOT A CONVENIENCE ─────────────────────────────────────────────────
 * A charge booked as a debt in one book can NEVER be settled in another — the ledger is append-only and the API
 * refuses to move a booked charge. So if a seam quietly defaults to dollars, that truck or examination can
 * never be paid in ZiG again. The API therefore REFUSES a charge-creating request that names no currency once
 * settlement is on, with a 422 that says "Which currency is this … being settled in?". This control is how the
 * operator answers it; without it they would meet a refusal they had no way to satisfy.
 *
 * ── IT RENDERS NOTHING WHILE SETTLEMENT IS OFF ──────────────────────────────────────────────────────────────
 * Which is production today, and for months. No extra step appears on any screen, no operator is asked anything
 * new, and every existing flow is untouched — the API treats a missing currency as the base exactly as before.
 * The moment the switch is turned on, the question appears at all seven seams at once.
 *
 * ── AND IT NEVER PRE-PICKS A NON-BASE BOOK ─────────────────────────────────────────────────────────────────
 * The default is the base. A screen that remembered the last choice, or defaulted to ZiG because the client's
 * account is in ZiG, would be one mis-click away from booking a dollar truck into the wrong book permanently.
 * The operator takes the money; the operator says which it was.
 */
@Component({
  selector: 'app-currency-choice',
  templateUrl: './currency-choice.component.html',
  styleUrls: ['./currency-choice.component.scss'],
})
export class CurrencyChoiceComponent implements OnInit {
  /** What is being settled, in the operator's words: "parking charge", "examination fee", "payment". */
  @Input() what = 'charge';

  /**
   * The book the record is ALREADY settled in, when it has one. The question is only a question while a record
   * is still open: a truck pre-paid in ZiG, or a gate pass Finance issued before the gate saw it, has already
   * decided the book, and the API will not re-ask. Shown as settled fact rather than a choice.
   */
  @Input() settled: string | null | undefined = null;

  /**
   * The book an EARLIER charge on the same record was settled in, which THIS charge follows.
   *
   * Distinct from {@link settled}, and the difference is not pedantry: a post-clear top-up has not been
   * settled in anything — it is being settled right now — but the truck it hangs off was settled at clearing,
   * and with no exchange rate one truck cannot carry 68,960 ZiG of parking and 40 USD of over-stay as any
   * kind of total. So the question is not skipped, it was already answered, and saying "already settled"
   * about the top-up itself would be telling the operator something untrue.
   */
  @Input() follows: string | null | undefined = null;

  /** What the followed book belongs to, in the operator's words: "truck", "examination". */
  @Input() followsWhat = 'record';

  /** Two-way: the code the operator picked, or the base while settlement is off. */
  @Input() value: string | null | undefined = null;
  @Output() valueChange = new EventEmitter<string | null>();

  @Input() disabled = false;

  constructor(private currency: CurrencyContextService) {}

  ngOnInit(): void {
    // Default to the base, never to the last thing anyone chose.
    if (!this.value) {
      this.emit(this.decidedCode ?? this.currency.base);
    }
  }

  /** Nothing is asked, and nothing is rendered, while settlement is off. */
  get asks(): boolean {
    return this.currency.settlementEnabled && !this.decidedCode;
  }

  /** The record's existing book, normalised — null when it has none. */
  get settledCode(): string | null {
    const code = (this.settled ?? '').trim().toUpperCase();

    return code === '' ? null : code;
  }

  /** The book an earlier charge decided, normalised — null when there is none. */
  get followsCode(): string | null {
    const code = (this.follows ?? '').trim().toUpperCase();

    return code === '' ? null : code;
  }

  /** The book this charge takes without asking, from either source. Its own stamp wins. */
  get decidedCode(): string | null {
    return this.settledCode ?? this.followsCode;
  }

  get choices(): string[] {
    // `settleable`, never `currencies`: the context endpoint returns every currency the system knows
    // whatever the switch says, and offering one the API would refuse is offering a dead end.
    return this.currency.settleable;
  }

  get base(): string {
    return this.currency.base;
  }

  /** Show the already-settled book as a fact, so the operator knows why they were not asked. */
  get showsSettled(): boolean {
    return this.currency.settlementEnabled && !!this.settledCode;
  }

  /** Show the INHERITED book as a fact — same reason, different sentence, because it is a different fact. */
  get showsFollows(): boolean {
    return this.currency.settlementEnabled && !this.settledCode && !!this.followsCode;
  }

  pick(code: string): void {
    if (this.disabled) {
      return;
    }
    this.emit(code);
  }

  isPicked(code: string): boolean {
    return (this.value ?? '').trim().toUpperCase() === code.trim().toUpperCase();
  }

  private emit(code: string | null): void {
    this.value = code;
    this.valueChange.emit(code);
  }
}
